/**
 * Input sanitization utilities to prevent XSS and injection attacks.
 * Strips HTML tags and control characters from user input.
 */

/** Strip HTML tags from a string */
export function stripHtml(input: string): string {
  return input.replace(/<[^>]*>/g, "");
}

/** Remove control characters (except newline/tab) */
function stripControlChars(input: string): string {
  return input.replace(/[\x00-\x08\x0B\x0C\x0E-\x1F\x7F]/g, "");
}

/** Sanitize a general text input (name, location, etc.) */
export function sanitizeText(input: string, maxLength: number = 100): string {
  return stripControlChars(stripHtml(input)).slice(0, maxLength);
}

/** Sanitize a phone number — digits, +, spaces, dashes only */
export function sanitizePhone(input: string, maxLength: number = 15): string {
  return input.replace(/[^\d+\-\s()]/g, "").slice(0, maxLength);
}

/** Sanitize a URL — must start with http(s):// or be empty */
export function sanitizeUrl(input: string): string {
  const trimmed = input.trim();
  if (!trimmed) return "";
  try {
    const url = new URL(trimmed);
    if (url.protocol === "http:" || url.protocol === "https:") return trimmed;
  } catch {}
  return "";
}

/** Sanitize a WhatsApp number — digits only */
export function sanitizeWhatsAppNumber(input: string): string {
  return input.replace(/\D/g, "").slice(0, 15);
}

/** Validate phone number for Ghana: exactly 10 digits local, or 12-13 with country code 233 */
export function isValidPhoneNumber(phone: string): boolean {
  const digits = phone.replace(/\D/g, "");
  // Ghana: 10 digits (0XXXXXXXXX) or 12 digits (233XXXXXXXXX) or 13 digits (+233XXXXXXXXX counted as 12)
  if (digits.length === 10) return true;
  if ((digits.length === 12 || digits.length === 13) && digits.startsWith("233")) return true;
  return false;
}
